import { AdSlotConfig, AdError, AdErrorType } from '../types/ads';

/**
 * PWA-specific ad service for handling offline caching, install prompts, and mobile optimizations
 */
export class PWAAdService {
  private serviceWorker: ServiceWorker | null = null;
  private isOffline: boolean = false;
  private installPromptEvent: any = null;
  private touchStartTime: number = 0;
  private touchStartPosition: { x: number; y: number } = { x: 0, y: 0 };

  constructor() {
    this.initializeServiceWorker();
    this.setupOfflineDetection();
    this.setupInstallPromptHandling();
    this.setupTouchOptimizations();
  }

  /**
   * Initialize service worker for ad caching
   */
  private async initializeServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        
        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;
        
        this.serviceWorker = registration.active;
        console.log('PWA Ad Service: Service worker initialized');
        
        // Listen for service worker messages
        navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage.bind(this));
        
      } catch (error) {
        console.error('PWA Ad Service: Failed to register service worker:', error);
      }
    }
  }

  /**
   * Setup offline detection
   */
  private setupOfflineDetection(): void {
    this.isOffline = !navigator.onLine;
    
    window.addEventListener('online', () => {
      this.isOffline = false;
      this.handleOnlineStateChange(true);
    });
    
    window.addEventListener('offline', () => {
      this.isOffline = true;
      this.handleOnlineStateChange(false);
    });
  }

  /**
   * Setup PWA install prompt handling
   */
  private setupInstallPromptHandling(): void {
    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.installPromptEvent = event;
      console.log('PWA Ad Service: Install prompt available');
    });

    window.addEventListener('appinstalled', () => {
      this.installPromptEvent = null;
      this.handleAppInstalled();
    });
  }

  /**
   * Setup mobile touch optimizations for ads
   */
  private setupTouchOptimizations(): void {
    // Add touch event listeners for better mobile ad interaction
    document.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    document.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
    document.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: true });
  }

  /**
   * Handle touch start for mobile ad optimization
   */
  private handleTouchStart(event: TouchEvent): void {
    if (this.isAdElement(event.target as Element)) {
      this.touchStartTime = Date.now();
      this.touchStartPosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
    }
  }

  /**
   * Handle touch end for mobile ad optimization
   */
  private handleTouchEnd(event: TouchEvent): void {
    if (this.isAdElement(event.target as Element)) {
      const touchDuration = Date.now() - this.touchStartTime;
      const touchEndPosition = {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY
      };
      
      const distance = Math.sqrt(
        Math.pow(touchEndPosition.x - this.touchStartPosition.x, 2) +
        Math.pow(touchEndPosition.y - this.touchStartPosition.y, 2)
      );
      
      // Prevent accidental clicks (too short or too much movement)
      if (touchDuration < 100 || distance > 10) {
        event.preventDefault();
        return;
      }
      
      // Track intentional ad interaction
      this.trackMobileAdInteraction(event.target as Element, {
        duration: touchDuration,
        distance,
        intentional: true
      });
    }
  }

  /**
   * Handle touch move for mobile ad optimization
   */
  private handleTouchMove(event: TouchEvent): void {
    // Prevent scrolling when touching ads to avoid accidental clicks
    if (this.isAdElement(event.target as Element)) {
      const distance = Math.sqrt(
        Math.pow(event.touches[0].clientX - this.touchStartPosition.x, 2) +
        Math.pow(event.touches[0].clientY - this.touchStartPosition.y, 2)
      );
      
      // Allow small movements but prevent large scrolls
      if (distance > 5) {
        event.preventDefault();
      }
    }
  }

  /**
   * Check if element is an ad element
   */
  private isAdElement(element: Element): boolean {
    if (!element) return false;
    
    return element.closest('.ad-slot') !== null ||
           element.closest('[id*="ad-"]') !== null ||
           element.closest('[class*="ad-"]') !== null ||
           element.closest('iframe[src*="googlesyndication"]') !== null ||
           element.closest('iframe[src*="doubleclick"]') !== null;
  }

  /**
   * Track mobile ad interaction
   */
  private trackMobileAdInteraction(element: Element, interaction: {
    duration: number;
    distance: number;
    intentional: boolean;
  }): void {
    const adSlot = element.closest('.ad-slot');
    const slotId = adSlot?.id || 'unknown';
    
    console.log('PWA Ad Service: Mobile ad interaction', {
      slotId,
      ...interaction,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });
    
    // Send interaction data to analytics
    if (window.gtag) {
      window.gtag('event', 'mobile_ad_interaction', {
        ad_slot_id: slotId,
        interaction_duration: interaction.duration,
        interaction_distance: interaction.distance,
        intentional_click: interaction.intentional
      });
    }
  }

  /**
   * Handle online/offline state changes
   */
  private handleOnlineStateChange(isOnline: boolean): void {
    console.log(`PWA Ad Service: Network state changed - ${isOnline ? 'online' : 'offline'}`);
    
    if (isOnline) {
      // Refresh ads when coming back online
      this.refreshAdsAfterOnline();
    } else {
      // Switch to cached/fallback ads when offline
      this.switchToOfflineAds();
    }
  }

  /**
   * Handle app installation
   */
  private handleAppInstalled(): void {
    console.log('PWA Ad Service: App installed');
    
    // Track installation event
    if (window.gtag) {
      window.gtag('event', 'pwa_installed', {
        event_category: 'PWA',
        event_label: 'App Installed'
      });
    }
    
    // Show install-specific ads or rewards
    this.showInstallRewardAds();
  }

  /**
   * Handle service worker messages
   */
  private handleServiceWorkerMessage(event: MessageEvent): void {
    const { data } = event;
    
    if (data.type === 'AD_CACHE_UPDATED') {
      console.log('PWA Ad Service: Ad cache updated', data.payload);
    } else if (data.type === 'AD_FALLBACK_USED') {
      console.log('PWA Ad Service: Fallback ad used', data.payload);
      this.trackFallbackAdUsage(data.payload);
    }
  }

  /**
   * Cache ad creative for offline use
   */
  async cacheAdCreative(url: string, html: string, metadata: any = {}): Promise<void> {
    if (!this.serviceWorker) {
      console.warn('PWA Ad Service: Service worker not available for caching');
      return;
    }

    try {
      navigator.serviceWorker.controller?.postMessage({
        type: 'CACHE_AD_CREATIVE',
        payload: { url, html, metadata }
      });
      
      console.log('PWA Ad Service: Ad creative cached', url);
    } catch (error) {
      console.error('PWA Ad Service: Failed to cache ad creative:', error);
    }
  }

  /**
   * Get offline ad fallback
   */
  async getOfflineAdFallback(slotConfig: AdSlotConfig): Promise<string> {
    const { sizes } = slotConfig;
    const primarySize = sizes[0];
    
    if (!primarySize) {
      return this.getGenericFallbackAd();
    }
    
    const [width, height] = primarySize;
    const fallbackKey = `banner-${width}x${height}`;
    
    try {
      const response = await fetch(`/fallback-ad/${fallbackKey}`);
      if (response.ok) {
        return await response.text();
      }
    } catch (error) {
      console.error('PWA Ad Service: Failed to get fallback ad:', error);
    }
    
    return this.getGenericFallbackAd(width, height);
  }

  /**
   * Get generic fallback ad HTML
   */
  private getGenericFallbackAd(width: number = 300, height: number = 250): string {
    return `
      <div style="
        width: ${width}px;
        height: ${height}px;
        background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #666;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${Math.min(width / 20, 14)}px;
        border: 1px solid #ddd;
        border-radius: 4px;
        flex-direction: column;
        text-align: center;
        padding: 10px;
        box-sizing: border-box;
      ">
        <div style="margin-bottom: 5px;">👗</div>
        <div>Fashion Advertisement</div>
        <div style="font-size: ${Math.min(width / 25, 12)}px; opacity: 0.7; margin-top: 5px;">
          Discover your style
        </div>
      </div>
    `;
  }

  /**
   * Show PWA install prompt with ad integration
   */
  async showInstallPromptWithAd(): Promise<boolean> {
    if (!this.installPromptEvent) {
      return false;
    }

    try {
      // Show install prompt
      await this.installPromptEvent.prompt();
      const { outcome } = await this.installPromptEvent.userChoice;
      
      // Track install prompt interaction
      if (window.gtag) {
        window.gtag('event', 'pwa_install_prompt', {
          event_category: 'PWA',
          event_label: outcome,
          value: outcome === 'accepted' ? 1 : 0
        });
      }
      
      if (outcome === 'accepted') {
        // Show reward ad or special offer
        this.showInstallRewardAds();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('PWA Ad Service: Install prompt failed:', error);
      return false;
    }
  }

  /**
   * Show install reward ads
   */
  private showInstallRewardAds(): void {
    // This could show special offers, discounts, or premium content
    console.log('PWA Ad Service: Showing install reward ads');
    
    // Example: Show a special fashion discount ad
    const rewardAdHtml = `
      <div style="
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 20px;
        border-radius: 8px;
        text-align: center;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        margin: 10px 0;
      ">
        <h3 style="margin: 0 0 10px 0;">🎉 Welcome to Hooliah!</h3>
        <p style="margin: 0 0 15px 0;">Get 20% off your first fashion purchase</p>
        <button style="
          background: white;
          color: #667eea;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          font-weight: bold;
          cursor: pointer;
        ">
          Claim Offer
        </button>
      </div>
    `;
    
    // Insert reward ad into the page
    const rewardContainer = document.createElement('div');
    rewardContainer.innerHTML = rewardAdHtml;
    rewardContainer.style.position = 'fixed';
    rewardContainer.style.top = '20px';
    rewardContainer.style.left = '50%';
    rewardContainer.style.transform = 'translateX(-50%)';
    rewardContainer.style.zIndex = '10000';
    rewardContainer.style.maxWidth = '90vw';
    
    document.body.appendChild(rewardContainer);
    
    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (rewardContainer.parentNode) {
        rewardContainer.parentNode.removeChild(rewardContainer);
      }
    }, 10000);
  }

  /**
   * Refresh ads after coming back online
   */
  private refreshAdsAfterOnline(): void {
    // Find all ad slots and refresh them
    const adSlots = document.querySelectorAll('.ad-slot');
    
    adSlots.forEach((slot) => {
      const slotId = slot.id;
      if (slotId && window.googletag) {
        window.googletag.cmd.push(() => {
          const gptSlots = window.googletag!.pubads().getSlots();
          const gptSlot = gptSlots.find(s => s.getSlotElementId() === slotId);
          if (gptSlot) {
            window.googletag!.pubads().refresh([gptSlot]);
          }
        });
      }
    });
    
    console.log('PWA Ad Service: Refreshed ads after coming online');
  }

  /**
   * Switch to offline/cached ads
   */
  private switchToOfflineAds(): void {
    const adSlots = document.querySelectorAll('.ad-slot');
    
    adSlots.forEach(async (slot) => {
      const slotId = slot.id;
      if (slotId) {
        try {
          // Try to get cached ad first
          const cachedResponse = await caches.match(`/ad/${slotId}`);
          if (cachedResponse) {
            const cachedHtml = await cachedResponse.text();
            slot.innerHTML = cachedHtml;
          } else {
            // Use fallback ad
            const fallbackHtml = await this.getOfflineAdFallback({
              slotId,
              sizes: [[300, 250]], // default size
              targeting: {},
              refreshRate: 0,
              lazy: false,
              priority: 1
            });
            slot.innerHTML = fallbackHtml;
          }
        } catch (error) {
          console.error(`PWA Ad Service: Failed to switch to offline ad for ${slotId}:`, error);
        }
      }
    });
    
    console.log('PWA Ad Service: Switched to offline ads');
  }

  /**
   * Track fallback ad usage
   */
  private trackFallbackAdUsage(payload: any): void {
    if (window.gtag) {
      window.gtag('event', 'fallback_ad_used', {
        event_category: 'Ads',
        event_label: payload.slotId || 'unknown',
        value: 1
      });
    }
  }

  /**
   * Get ad cache status
   */
  async getAdCacheStatus(): Promise<any> {
    if (!this.serviceWorker) {
      return { error: 'Service worker not available' };
    }

    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data);
      };
      
      navigator.serviceWorker.controller?.postMessage(
        { type: 'GET_AD_CACHE_STATUS' },
        [messageChannel.port2]
      );
      
      // Timeout after 5 seconds
      setTimeout(() => {
        resolve({ error: 'Timeout' });
      }, 5000);
    });
  }

  /**
   * Clear ad cache
   */
  async clearAdCache(): Promise<void> {
    if (!this.serviceWorker) {
      console.warn('PWA Ad Service: Service worker not available for cache clearing');
      return;
    }

    navigator.serviceWorker.controller?.postMessage({
      type: 'CLEAR_AD_CACHE'
    });
    
    console.log('PWA Ad Service: Ad cache clear requested');
  }

  /**
   * Check if app is installed as PWA
   */
  isInstalled(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true;
  }

  /**
   * Check if install prompt is available
   */
  canInstall(): boolean {
    return this.installPromptEvent !== null;
  }

  /**
   * Check if currently offline
   */
  isOfflineMode(): boolean {
    return this.isOffline;
  }

  /**
   * Optimize ad for mobile viewport
   */
  optimizeAdForMobile(slotConfig: AdSlotConfig): AdSlotConfig {
    const isMobile = window.innerWidth <= 768;
    
    if (!isMobile) {
      return slotConfig;
    }
    
    // Mobile-specific optimizations
    const optimizedConfig = { ...slotConfig };
    
    // Adjust sizes for mobile
    optimizedConfig.sizes = slotConfig.sizes.filter(([width, height]) => {
      return width <= window.innerWidth && height <= window.innerHeight / 3;
    });
    
    // Add mobile-specific targeting
    optimizedConfig.targeting = {
      ...slotConfig.targeting,
      device_type: 'mobile',
      viewport_width: window.innerWidth.toString(),
      viewport_height: window.innerHeight.toString(),
      touch_enabled: 'ontouchstart' in window ? 'true' : 'false'
    };
    
    // Enable lazy loading for mobile performance
    optimizedConfig.lazy = true;
    
    return optimizedConfig;
  }
}

// Export singleton instance
export const pwaAdService = new PWAAdService();